import React from 'react';
import { Card, Col, Empty, Row, Space, Tag, Typography } from 'antd';
import {
    ExperimentOutlined,
    CalendarOutlined,
    ToolOutlined,
    MedicineBoxFilled
} from '@ant-design/icons';
import HtmlParser from 'react-html-parser';
import { isMobile } from 'react-device-detect';
import { renderDate, renderLaboCaseStatusTag } from '@/utils/helpers';

const { Text, Title, Paragraph } = Typography;

const InfoItem = ({ label, value, span = 8 }) => (
    <Col xs={24} sm={12} md={span}>
        <div style={{ marginBottom: 8 }}>
            <Text type="secondary" style={{ display: 'block' }}>
                {label}
            </Text>
            <div style={{ marginTop: 2 }}>{value || <Text type="secondary">--</Text>}</div>
        </div>
    </Col>
);

const CustomerServiceLaboCases = ({ services = [] }) => {
    const safeServices = (Array.isArray(services) ? services : []).filter(
        (item) => Array.isArray(item?.labo_cases) && item.labo_cases.length > 0
    );

    if (!safeServices.length) {
        return (
            <Card
                title={
                    <Space>
                        <ExperimentOutlined />
                        <span>Theo dõi labo theo dịch vụ</span>
                    </Space>
                }
                bordered={false}
            >
                <Empty description="Khách hàng chưa có dịch vụ nào để theo dõi labo" />
            </Card>
        );
    }

    return (
        <div className='mt-4 mb-4'>
            <Title level={4}>Theo dõi labo theo dịch vụ</Title>

            <div className='w-100'>
                {safeServices.map((serviceItem) => {
                    const laboCases = Array.isArray(serviceItem?.labo_cases) ? serviceItem.labo_cases : [];
                    const serviceName = serviceItem?.service?.name || `Dịch vụ #${serviceItem?.id}`;

                    return (
                        <Card
                            key={serviceItem.id}
                            size="small"
                            bordered
                            style={{
                                borderRadius: 10,
                                background: '#f3f3f3',
                                marginBottom: 16
                            }}
                            bodyStyle={{ padding: 0 }}
                            title={
                                <Space wrap>
                                    <MedicineBoxFilled />
                                    <span>{serviceName}</span>

                                    <Tag color="purple">
                                        {laboCases.length} labo case
                                    </Tag>

                                    {(serviceItem?.converted_from_service_name || serviceItem?.converted_to_service_name) && (
                                        <Text type="secondary">
                                            {serviceItem?.converted_from_service_name ? (
                                                <>
                                                    Chuyển từ: <strong>{serviceItem.converted_from_service_name}</strong>
                                                </>
                                            ) : null}
                                            {serviceItem?.converted_from_service_name && serviceItem?.converted_to_service_name ? ' → ' : ''}
                                            {serviceItem?.converted_to_service_name ? (
                                                <>
                                                    <strong>{serviceItem.converted_to_service_name}</strong>
                                                </>
                                            ) : null}
                                        </Text>
                                    )}
                                </Space>
                            }
                        >
                            <div className='w-100'>
                                {laboCases.map((laboCase, index) => {
                                    const laboCaseStatus = renderLaboCaseStatusTag(laboCase?.status);
                                    const labo = laboCase?.labo || {};

                                    return (
                                        <React.Fragment key={laboCase.id || `${serviceItem.id}-${index}`}>
                                            <Card
                                                size="small"
                                                bordered={false}
                                                style={{
                                                    background: '#f3f3f3',
                                                    borderRadius: 0
                                                }}
                                                bodyStyle={{ padding: isMobile ? 12 : 14 }}
                                            >
                                                <Row gutter={[12, 12]}>
                                                    <Col xs={24} lg={10}>
                                                        <Space direction="vertical" size={6} style={{ width: '100%' }}>
                                                            <Space wrap>
                                                                <ToolOutlined />
                                                                <Text strong>
                                                                    {labo?.name || `Labo #${laboCase?.labo_id || '--'}`}
                                                                </Text>
                                                                {laboCaseStatus}
                                                            </Space>

                                                            <Text type="secondary">
                                                                Điện thoại: {labo?.phone || '--'}
                                                            </Text>

                                                            <Text type="secondary">
                                                                Địa chỉ: {labo?.address || '--'}
                                                            </Text>

                                                            <Text type="secondary">
                                                                Bảo hành: {labo?.warranty_months ?? 0} tháng
                                                            </Text>
                                                        </Space>
                                                    </Col>

                                                    <Col xs={24} lg={14}>
                                                        <Row gutter={[12, 8]}>
                                                            <InfoItem
                                                                label="Ngày gửi labo"
                                                                value={
                                                                    <Space size={4}>
                                                                        <CalendarOutlined />
                                                                        <span>{renderDate(laboCase?.send_date, false)}</span>
                                                                    </Space>
                                                                }
                                                            />
                                                            <InfoItem
                                                                label="Ngày nhận từ labo"
                                                                value={renderDate(laboCase?.receive_date, false)}
                                                            />
                                                            <InfoItem
                                                                label="Ngày trả"
                                                                value={renderDate(laboCase?.return_date, false)}
                                                            />
                                                            <InfoItem
                                                                label="Ngày tạo case"
                                                                value={renderDate(laboCase?.created_at, false)}
                                                            />
                                                        </Row>
                                                    </Col>
                                                </Row>

                                                {!!laboCase?.note && (
                                                    <>
                                                        <div
                                                            style={{
                                                                borderTop: '1px dashed #d9d9d9',
                                                                margin: '12px 0',
                                                            }}
                                                        />
                                                        <Paragraph style={{ marginBottom: 0 }}>
                                                            <Text strong>Ghi chú labo case: </Text>
                                                            <span>{HtmlParser(laboCase.note)}</span>
                                                        </Paragraph>
                                                    </>
                                                )}
                                            </Card>

                                            {index < laboCases.length - 1 && (
                                                <div
                                                    style={{
                                                        borderTop: '4px solid #fff',
                                                        margin: '0 12px',
                                                    }}
                                                />
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </div>
                        </Card>
                    );
                })}
            </div>
        </div>
    );
};

export default CustomerServiceLaboCases;